"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Cpu, BrainCircuit, BookOpen, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tracks = [{
  title: "AI & Automation",
  description: "Harness the power of intelligent algorithms to streamline operations and drive unprecedented efficiency in your business workflows.",
  icon: BrainCircuit,
  link: "/SolutionsPage",
  animation: "group-hover:rotate-12 group-hover:scale-110"
}, {
  title: "Custom Software",
  description: "Bespoke digital solutions engineered from the ground up to solve your unique challenges and scale with your enterprise growth.",
  icon: Cpu,
  link: "/SolutionsPage",
  animation: "group-hover:scale-110"
}, {
  title: "Cybersecurity",
  description: "Fortify your digital perimeter with advanced threat detection and robust security protocols designed for the modern landscape.",
  icon: BookOpen,
  link: "/SolutionsPage",
  animation: "group-hover:-translate-y-1 group-hover:scale-110"
}];
export default function NextGenCapabilities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223718" id="next-gen-capabilities" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-lg blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-lg blur-3xl pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <span className="font-mono text-primary text-sm uppercase tracking-widest mb-4 block">
            Technological Excellence
          </span>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 tracking-tight">
            Next-Gen Capabilities
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg">
            Apex IT Solutions delivers futuristic, premium technology services designed to propel your business into the digital vanguard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tracks.map((track, i) => <Card key={i} data-index={i} className="group relative bg-card/30 backdrop-blur-xl border-border/40 transition-all duration-500 hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/10 overflow-hidden flex flex-col h-full rounded-md">
              <CardHeader className="pb-4">
                <div className="mb-6 inline-flex p-4 rounded-md bg-primary/5 text-primary transition-colors duration-500 group-hover:bg-primary/15 w-fit">
                  <track.icon className={`w-8 h-8 transition-all duration-500 ease-out ${track.animation}`} />
                </div>
                <CardTitle className="font-serif text-2xl tracking-tight">
                  {track.title}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-grow pb-2">
                <CardDescription className="font-sans text-base leading-relaxed text-muted-foreground/70 group-hover:text-muted-foreground transition-colors duration-300">
                  {track.description}
                </CardDescription>
              </CardContent>

              <CardFooter className="pt-0">
                <div className="w-full grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-in-out">
                  <div className="overflow-hidden">
                    <div className="pt-6 translate-y-4 group-hover:translate-y-0 transition-all duration-500 ease-out opacity-0 group-hover:opacity-100">
                      <Button asChild className="w-full group/btn border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-md" variant="outline">
                        <Link to={track.link}>
                          View Solution
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}